import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  ChatBubbleLeftIcon,
  UserIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiUtils';
import LoadingSpinner from '../common/LoadingSpinner';
import ActivityItem from './ActivityItem';

/**
 * ActivityFeed Component
 *
 * Displays a timeline of all activities for an issue, including:
 * - Comments
 * - Assignment changes
 * - Status changes
 * - Resolution
 * - Other field changes
 */
const ActivityFeed = ({ issueId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'comments', 'changes'
  const [pollInterval, setPollInterval] = useState(null);

  // Load activities
  const loadActivities = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/issues/${issueId}/activities`);

      if (response.success) {
        setActivities(response.activities || []);
      }
    } catch (error) {
      // Silent failure
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (issueId) {
      loadActivities();

      // Poll for updates every 60 seconds (optimized from 30s)
      // Only poll when page is visible
      const interval = setInterval(() => {
        if (!document.hidden) {
          loadActivities();
        }
      }, 60000);

      setPollInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [issueId]);

  // Filter activities based on selected filter
  const filteredActivities = activities.filter((activity) => {
    if (filter === 'all') return true;
    if (filter === 'comments') return activity.activity_type === 'comment';
    if (filter === 'changes') return activity.activity_type !== 'comment';
    return true;
  });

  // Get unique activity types for filtering
  const hasComments = activities.some(a => a.activity_type === 'comment');
  const hasChanges = activities.some(a => a.activity_type !== 'comment');

  return (
    <div className="space-y-4">
      {/* Header with Filters */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-secondary-900 flex items-center">
          <ClockIcon className="h-5 w-5 mr-2" />
          Activity
          <span className="ml-2 text-sm font-normal text-secondary-500">
            ({filteredActivities.length})
          </span>
        </h3>

        {/* Filter Buttons */}
        {(hasComments || hasChanges) && (
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-4 w-4 text-secondary-400" />
            <div className="flex space-x-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  filter === 'all'
                    ? 'bg-primary-100 text-primary-800'
                    : 'text-secondary-600 hover:bg-secondary-100'
                }`}
              >
                All
              </button>
              {hasComments && (
                <button
                  onClick={() => setFilter('comments')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center ${
                    filter === 'comments'
                      ? 'bg-primary-100 text-primary-800'
                      : 'text-secondary-600 hover:bg-secondary-100'
                  }`}
                >
                  <ChatBubbleLeftIcon className="h-3 w-3 mr-1" />
                  Comments
                </button>
              )}
              {hasChanges && (
                <button
                  onClick={() => setFilter('changes')}
                  className={`px-3 py-1 text-xs rounded-md transition-colors flex items-center ${
                    filter === 'changes'
                      ? 'bg-primary-100 text-primary-800'
                      : 'text-secondary-600 hover:bg-secondary-100'
                  }`}
                >
                  <UserIcon className="h-3 w-3 mr-1" />
                  Changes
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Activity Timeline */}
      <div className="bg-white rounded-lg border border-secondary-200">
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="md" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <ClockIcon className="h-10 w-10 text-secondary-300 mx-auto mb-3" />
            <h4 className="text-sm font-medium text-secondary-900 mb-1">
              {filter === 'all' ? 'No activity yet' : `No ${filter} yet`}
            </h4>
            <p className="text-sm text-secondary-500">
              {filter === 'all'
                ? 'Activity will appear here as changes are made to this issue'
                : `${filter === 'comments' ? 'Comments' : 'Changes'} will appear here as they occur`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-secondary-100">
            {filteredActivities.map((activity, index) => (
              <ActivityItem
                key={activity.id}
                activity={activity}
                isLast={index === filteredActivities.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;
